import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useAuthStore } from '@/state/store/auth';
import {
  blacklistContact,
  blacklistContacts,
  getBlacklistedContacts,
  getContactListCounts,
  getContacts,
  insertManyContact,
  unblacklistContact,
  unblacklistContacts,
} from '../services/contacts.service';
import {
  createContactFromRest,
  deleteContactFromRest,
  deleteContactsFromRest,
  getContactByIdFromRest,
  getContactsFromRest,
  updateContactFromRest,
  updateContactSubscriptionsFromRest,
} from '../services/contacts-rest.service';
import type { ContactsFilters } from '../types/contacts-filters.types';
import type {
  Contact,
  ContactInsertInput,
  ContactsPagingResult,
  GetContactsParams,
  InsertContactResult,
  InsertManyContactResult,
  UpdateContactResult,
} from '../types/contacts.types';
import type {
  CreateContactRequestApi,
  UpdateContactRequestApi,
  UpdateSubscriptionStatusRequestApi,
} from '../types/contacts-api.types';
import { getContactBlacklistedByIds } from '../utils/contact-attributes';
import type { FilterFieldConfig } from '../config/filterable-fields';
import type {
  FilterMatchType,
  FilterRow,
} from '../components/filter-row-builder/filter-row-builder';
import {
  buildContactsRestFilterValues,
  type ContactsRestFilters,
} from '../utils/contacts-rest-filter.utils';

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (debouncedValue === value) return;
    const timer = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [debouncedValue, delayMs, value]);

  return debouncedValue;
};

const invalidateContactQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['contacts'] });
  queryClient.invalidateQueries({ queryKey: ['contact'] });
  queryClient.invalidateQueries({ queryKey: ['blacklisted-contacts'] });
  queryClient.invalidateQueries({ queryKey: ['contact-list-counts'] });
  queryClient.invalidateQueries({ queryKey: ['lists'] });
  queryClient.invalidateQueries({ queryKey: ['list'] });
  queryClient.invalidateQueries({ queryKey: ['list-contacts'] });
};

export const useGetContacts = (
  params: GetContactsParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<ContactsPagingResult>({
    queryKey: ['contacts', params],
    queryFn: () => getContacts(params),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
};

export type UseSearchContactsFilterParams = {
  emailStatusFilters?: string[];
  smsStatusFilters?: string[];
  listId?: string;
  fieldFilters?: ContactsFilters;
  advancedRows?: FilterRow[];
  advancedMatchType?: FilterMatchType;
  registry?: FilterFieldConfig[];
  /** When set, forwarded as `suppressed=true|false` on GET /v1/contacts. */
  suppressed?: boolean;
};

export const useSearchContacts = (
  search: string,
  paging: { pageNo?: number; pageSize?: number } = { pageNo: 1, pageSize: 10 },
  filterParams: UseSearchContactsFilterParams = {}
) => {
  const debouncedSearch = useDebouncedValue(search, 300);
  const pageNo = paging.pageNo ?? 1;
  const pageSize = paging.pageSize ?? 10;
  const trimmedSearch = debouncedSearch.trim();
  const suppressed = filterParams.suppressed;
  const restFilter: ContactsRestFilters = buildContactsRestFilterValues({
    emailStatusFilters: filterParams.emailStatusFilters,
    smsStatusFilters: filterParams.smsStatusFilters,
    listId: filterParams.listId,
    fieldFilters: filterParams.fieldFilters,
    advancedRows: filterParams.advancedRows,
    registry: filterParams.registry,
  });

  return useGlobalQuery<ContactsPagingResult>({
    // Key shape ['contacts','rest',pageNo,pageSize,search,filterJson] is matched positionally by
    // the cache-eviction predicate in contacts.tsx's handlePageSizeChange — don't reorder it.
    // `suppressed` is appended after filterJson so Task 6 can toggle without shifting positions.
    queryKey: [
      'contacts',
      'rest',
      pageNo,
      pageSize,
      trimmedSearch,
      JSON.stringify(restFilter),
      suppressed ?? null,
    ],
    // One endpoint for every case. Previously this branched to `/v1/contacts/search` whenever a
    // search term or filter was present, purely because `/v1/contacts` accepted neither; both now
    // narrow the same request, and only `/v1/contacts` reports pageInfo.totalUnfiltered.
    queryFn: () =>
      getContactsFromRest({
        q: trimmedSearch || undefined,
        filter: restFilter,
        pageNo,
        pageSize,
        ...(suppressed !== undefined ? { suppressed } : {}),
      }),
    // Always refetch, never serve a cached page (from dev, commit 01cde6c). The table has to show
    // a contact the user just added or edited, and the mutations invalidate by key prefix rather
    // than refetching this exact key, so a stale window here is visible as a missing row.
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useInfiniteSearchContacts = (
  search: string,
  options: { pageSize?: number; enabled?: boolean } = {}
) => {
  const debouncedSearch = useDebouncedValue(search, 300);
  const pageSize = options.pageSize ?? 10;
  const trimmedSearch = debouncedSearch.trim();

  return useInfiniteQuery<
    ContactsPagingResult,
    Error,
    InfiniteData<ContactsPagingResult, number>,
    readonly ['contacts', 'rest', 'infinite', number, string],
    number
  >({
    queryKey: ['contacts', 'rest', 'infinite', pageSize, trimmedSearch],
    queryFn: ({ pageParam }) =>
      getContactsFromRest({
        q: trimmedSearch || undefined,
        pageNo: pageParam,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.pageNo + 1 : undefined),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
};

export const useGetContactById = (itemId: string) => {
  // Plain query: the detail page renders its own not-found / error state, so the global
  // error toast would only add a redundant "Something went wrong — Not found".
  return useQuery<Contact>({
    queryKey: ['contact', 'rest', itemId],
    queryFn: () => getContactByIdFromRest(itemId),
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetContactDetails = (itemId: string) => {
  const query = useGetContactById(itemId);

  return {
    ...query,
    contact: query.data,
  };
};

export const useContactListCounts = (listNames: readonly string[], enabled = true) => {
  return useGlobalQuery<Record<string, number>>({
    queryKey: ['contact-list-counts', listNames],
    queryFn: () => getContactListCounts(listNames),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && listNames.length > 0,
  });
};

export const useBlacklistedContacts = (
  paging: { pageNo?: number; pageSize?: number } = { pageNo: 1, pageSize: 100 },
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<ContactsPagingResult>({
    queryKey: ['blacklisted-contacts', paging],
    queryFn: () => getBlacklistedContacts(paging),
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
  });
};

export const useInsertContact = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<InsertContactResult, Error, CreateContactRequestApi>({
    mutationFn: createContactFromRest,
    // The contact drawer reports failures itself, on the field when the API names one.
    suppressToast: true,
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useInsertManyContact = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<InsertManyContactResult, Error, ContactInsertInput[]>({
    mutationFn: insertManyContact,
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    UpdateContactResult,
    Error,
    { contactId: string; input: UpdateContactRequestApi }
  >({
    mutationFn: ({ contactId, input }) => updateContactFromRest(contactId, input),
    // The contact drawer reports failures itself, on the field when the API names one.
    suppressToast: true,
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useUpdateContactSubscriptions = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    UpdateContactResult,
    Error,
    { contactId: string; input: UpdateSubscriptionStatusRequestApi }
  >({
    mutationFn: ({ contactId, input }) => updateContactSubscriptionsFromRest(contactId, input),
    onSuccess: (result, { contactId }) => {
      if (result.contact) {
        queryClient.setQueryData(['contact', 'rest', contactId], result.contact);
      }
      queryClient.invalidateQueries({ queryKey: ['contact-activity', contactId] });
      invalidateContactQueries(queryClient);
    },
  });
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateContactResult, Error, { contactId: string }>({
    mutationFn: ({ contactId }) => deleteContactFromRest(contactId),
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useDeleteContacts = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    { acknowledged: boolean; totalImpactedData: number; message?: string },
    Error,
    { contactIds: string[] }
  >({
    mutationFn: ({ contactIds }) => deleteContactsFromRest(contactIds),
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useBlacklistContact = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useGlobalMutation<UpdateContactResult, Error, Contact | string>({
    mutationFn: (contactOrId) => {
      const userId = user?.itemId;
      if (!userId) {
        throw new Error('You must be signed in to blacklist a contact');
      }

      if (typeof contactOrId === 'string') {
        return blacklistContact(contactOrId, userId);
      }

      return blacklistContact(contactOrId.ItemId, userId, getContactBlacklistedByIds(contactOrId));
    },
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useBlacklistContacts = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useGlobalMutation<UpdateContactResult[], Error, Contact[]>({
    mutationFn: (contacts) => {
      const userId = user?.itemId;
      if (!userId) {
        throw new Error('You must be signed in to blacklist contacts');
      }

      return blacklistContacts(
        contacts.map((contact) => ({
          contactId: contact.ItemId,
          existingBlacklistedBy: getContactBlacklistedByIds(contact),
        })),
        userId
      );
    },
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useUnblacklistContact = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateContactResult, Error, string>({
    mutationFn: (contactId) => unblacklistContact(contactId),
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};

export const useUnblacklistContacts = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateContactResult[], Error, string[]>({
    mutationFn: (contactIds) => unblacklistContacts(contactIds),
    onSuccess: () => {
      invalidateContactQueries(queryClient);
    },
  });
};
